import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import * as React from "react";

import { datePickerFormat, useDateTimeFormat } from "@/shared/datetime";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Day.js adapters that answer a fixed clock: a picker given no `ampm` prop asks
 * its adapter whether the locale uses a 12-hour clock. Both are declared at
 * module scope because `LocalizationProvider` builds a new adapter whenever the
 * `dateAdapter` class or `dateFormats` object changes, so a stable class keeps
 * the adapter until the clock or the typed date format changes.
 */
class AdapterDayjs12h extends AdapterDayjs {
  constructor(...args: ConstructorParameters<typeof AdapterDayjs>) {
    super(...args);
    this.is12HourCycleInCurrentLocale = () => true;
  }
}

class AdapterDayjs24h extends AdapterDayjs {
  constructor(...args: ConstructorParameters<typeof AdapterDayjs>) {
    super(...args);
    this.is12HourCycleInCurrentLocale = () => false;
  }
}

/**
 * Makes every MUI date picker below it type and show dates in the signed-in
 * user's date/time preference: its date order and separator, and its 12- or
 * 24-hour clock, in both the typed value and the empty field's hint.
 */
export const Localization: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { preference } = useDateTimeFormat();
  const { keyboardDate, ampm } = React.useMemo(
    () => datePickerFormat(preference),
    [preference],
  );
  const dateFormats = React.useMemo(() => ({ keyboardDate }), [keyboardDate]);
  // MUI hints an empty hours section as `hh` on either clock; a 24-hour field
  // takes Day.js's 24-hour token instead, so the hint says which clock to type.
  const localeText = React.useMemo(
    () => ({ fieldHoursPlaceholder: () => (ampm ? "hh" : "HH") }),
    [ampm],
  );

  return (
    <LocalizationProvider
      dateAdapter={ampm ? AdapterDayjs12h : AdapterDayjs24h}
      dateFormats={dateFormats}
      localeText={localeText}
    >
      {children}
    </LocalizationProvider>
  );
};
